import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  DeactivateNewsCategoryImagePayload,
  DeactivateNewsImagePayload,
  ManageNewsCategoryPayload,
  ManageNewsPayload,
  NewsCategory,
  NewsDetail,
  NewsTableQuery,
  NewsTableResponse,
  UploadNewsCategoryImagePayload,
  UploadNewsImagePayload,
} from '../../../models/cityparking/news.model';

@Injectable({ providedIn: 'root' })
export class ApiNewsService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  private get token(): string {
    return this.cookieService.get('token');
  }

  getNews(query: NewsTableQuery): Observable<NewsTableResponse> {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.visibility) params['visibility'] = query.visibility;
    if (query.status) params['status'] = query.status;
    if (query.siteId) params['siteId'] = query.siteId;
    if (query.categoryId) params['categoryId'] = query.categoryId;
    if (query.search) params['search'] = query.search;

    return this.apiMethodService.request<NewsTableResponse>(
      '/Backoffice/getNews',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getNews',
        token: this.token,
        headerOnly: false,
        params,
      }
    );
  }

  /** บอร์ดข่าวพนักงาน — published (public + internal) */
  getStaffNewsForBoard(query: NewsTableQuery): Observable<NewsTableResponse> {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.visibility) params['visibility'] = query.visibility;
    if (query.siteId) params['siteId'] = query.siteId;
    if (query.categoryId) params['categoryId'] = query.categoryId;
    if (query.search) params['search'] = query.search;

    return this.apiMethodService.request<NewsTableResponse>(
      '/Backoffice/getStaffNewsForBoard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getStaffNewsForBoard',
        token: this.token,
        headerOnly: false,
        params,
      }
    );
  }

  getStaffNewsById(id: number, siteId?: number): Observable<NewsDetail> {
    const params: Record<string, string | number> = { id };
    if (siteId && siteId > 0) params['siteId'] = siteId;

    return this.apiMethodService.request<NewsDetail>(
      '/Backoffice/getStaffNewsById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getStaffNewsById',
        token: this.token,
        headerOnly: false,
        params,
      }
    );
  }

  getNewsById(id: number): Observable<NewsDetail> {
    return this.apiMethodService.request<NewsDetail>(
      '/Backoffice/getNewsById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getNewsById',
        token: this.token,
        headerOnly: false,
        params: { id },
      }
    );
  }

  getNewsCategories(activeOnly = true): Observable<NewsCategory[]> {
    return this.apiMethodService.request<NewsCategory[]>(
      '/Backoffice/getNewsCategories',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getNewsCategories',
        token: this.token,
        headerOnly: false,
        params: { activeOnly: activeOnly ? 'true' : 'false' },
      }
    );
  }

  manageNews(payload: ManageNewsPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/manageNews',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'manageNews',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  manageNewsCategory(payload: ManageNewsCategoryPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/manageNewsCategory',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'manageNewsCategory',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadNewsCategoryImage(payload: UploadNewsCategoryImagePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/uploadNewsCategoryImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadNewsCategoryImage',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deactivateNewsCategoryImage(payload: DeactivateNewsCategoryImagePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/deactivateNewsCategoryImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deactivateNewsCategoryImage',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadNewsImage(payload: UploadNewsImagePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/uploadNewsImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadNewsImage',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deactivateNewsImage(payload: DeactivateNewsImagePayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/Backoffice/deactivateNewsImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deactivateNewsImage',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }
}
